import {
  INSIGHTS_FACEBOOK_TIME_SERIES_DATE_FIELDS,
  InsightsFacebookDateField,
  InsightsFilter,
  InsightsFilterDate,
  InsightsFilterExpression,
} from '@magicbrief/common';

export function convertDateMetricValues(filter: InsightsFilterDate) {
  if (
    INSIGHTS_FACEBOOK_TIME_SERIES_DATE_FIELDS.includes(
      filter.field as InsightsFacebookDateField
    )
  ) {
    switch (filter.operation) {
      case '=':
      case '!=':
      case '<':
      case '>': {
        return {
          field: filter.field,
          operation: filter.operation,
          value: new Date(filter.value),
        };
      }
      case 'between':
      case 'notBetween':
        return {
          field: filter.field,
          operation: filter.operation,
          start: new Date(filter.start),
          end: new Date(filter.end),
        };
      case 'withinLast':
      case 'withinDatePreset':
      default:
        return filter;
    }
  }

  return filter;
}

export function convertAdvancedFilterDateExpressions(
  filter: InsightsFilterExpression
) {
  return filter.map((f) => {
    return {
      operator: f.operator,
      operand:
        'expressions' in f.operand
          ? {
              expressions: f.operand.expressions.map((exp) => {
                if (
                  !INSIGHTS_FACEBOOK_TIME_SERIES_DATE_FIELDS.includes(
                    (exp.operand as InsightsFilter)
                      .field as InsightsFacebookDateField
                  )
                )
                  return exp;

                return {
                  operator: exp.operator,
                  operand: convertDateMetricValues(
                    exp.operand as InsightsFilterDate
                  ),
                };
              }),
            }
          : f.operand,
    };
  });
}

type WithPossibleDates<T> = T extends Date
  ? T | string
  : T extends Array<infer U>
    ? Array<WithPossibleDates<U>>
    : T extends object
      ? { [K in keyof T]: WithPossibleDates<T[K]> }
      : T;

// Recursive function to convert all Date objects to strings within an object
export function convertDatesToStrings<T>(obj: T): WithPossibleDates<T> {
  if (obj === null || obj === undefined) {
    return obj as WithPossibleDates<T>;
  }

  if (obj instanceof Date) {
    return obj.toISOString() as WithPossibleDates<T>;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertDatesToStrings) as WithPossibleDates<T>;
  }

  if (typeof obj === 'object') {
    const result = {} as Record<string, unknown>;

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = convertDatesToStrings(
          (obj as Record<string, unknown>)[key]
        );
      }
    }

    return result as WithPossibleDates<T>;
  }

  return obj as WithPossibleDates<T>;
}
